'use client';

import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Flex } from '@ironhack/design-system2/components/flex';
import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import { HighlightedCard } from '@ironhack/design-system2/components/highlighted-card';
import { SimpleGrid } from '@ironhack/design-system2/components/grid';
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@ironhack/design-system2/components/tab';
import { Text, type TextProps } from '@ironhack/design-system2/components/text';
import { useBreakpointValue } from '@ironhack/design-system2/hooks';
import { Fragment, type ReactElement } from 'react';

import { Button } from '@/app/components/client/button';
import { CmsImage } from '@/app/components/server/cms-image';
import { Link } from '@/app/components/client/link/link';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { StructuredContent } from '@/app/components/client/structured-content';
import {
  type DatoButtonBlock,
  type DatoHighlightedCard,
  type DatoModuleFormat,
} from '@/lib/datocms';

export type Bullets = {
  button?: [DatoButtonBlock];
  cardSize: 'small' | 'large';
  content: Array<{
    cards?: DatoHighlightedCard[];
    tabs?: Array<{
      cards: DatoHighlightedCard[];
      title: string;
      titleCallout?: string;
    }>;
  }>;
  hideSingleTab?: boolean;
  headingProps?: TextProps | null;
  moduleFormat: [DatoModuleFormat];
  subtitle?: string;
  title: string;
};

type Props = { data: Bullets } & BoxProps;

export const Bullets = forwardRef((props: Props, ref): ReactElement => {
  const {
    as = 'section',
    data: {
      button: [button] = [],
      cardSize,
      content: [{ tabs, cards }] = [{}],
      headingProps,
      hideSingleTab = false,
      moduleFormat: [moduleFormat],
      subtitle,
      title,
    },
    ...boxProps
  } = props;

  const colorsMap = ['brand.primary', 'icon.warning', 'fill.new'];
  const isMobile = useBreakpointValue({ base: true, md: false });
  const size = isMobile ? 'full' : cardSize === 'small' ? '2xl' : '5xl';

  const shouldShowTabs = tabs && (tabs.length > 1 || !hideSingleTab);

  const renderCard = (card: DatoHighlightedCard, index: number) => {
    const { content, image, target: [target] = [], ...cardData } = card;
    const Wrap = target
      ? {
          Component: Link,
          props: { withoutDecoration: true, ...target },
        }
      : { Component: Fragment, props: {} };

    return (
      <Wrap.Component key={cardData.title} {...Wrap.props}>
        <HighlightedCard
          borderColor={colorsMap[index % 3]}
          content={
            typeof content === 'string' ? (
              content
            ) : (
              <StructuredContent data={content} textStyle="s" />
            )
          }
          image={
            image && (
              <CmsImage
                data={image.responsiveImage}
                sizes="68px"
                {...(as === 'header' && { priority: true })}
              />
            )
          }
          size={size}
          {...cardData}
        />
      </Wrap.Component>
    );
  };

  return (
    <ModuleWrapper
      innerProps={{
        align: ['start', null, 'center'],
        direction: 'column',
        gap: 3,
        px: [2, null, 10],
      }}
      moduleFormat={moduleFormat}
      outerProps={{ as, ...boxProps }}
      ref={ref}
    >
      <Text
        as="h2"
        // Make sure it's 60% width in desktop in spite of outer padding
        mx={[0, null, 'calc(20% - var(--chakra-space-6))']}
        textAlign={['start', null, 'center']}
        textStyle={['3xl', null, '4xl']}
        {...headingProps}
      >
        {title}
      </Text>
      {subtitle && (
        <Text
          // Make sure it's 60% width in desktop in spite of outer padding
          mx={[0, null, 'calc(20% - var(--chakra-space-6))']}
          textAlign={['start', null, 'center']}
          textStyle="l"
        >
          {subtitle}
        </Text>
      )}
      {tabs ? (
        <Tabs variant="unstyled" w="full">
          {shouldShowTabs && (
            <TabList gap={3} justifyContent={['start', 'center']}>
              {tabs.map(({ title: tabTitle, titleCallout }) => (
                <Tab
                  _selected={{ borderColor: 'fill.interactive' }}
                  borderBottom="4px"
                  borderColor="transparent"
                  fontSize="l"
                  key={tabTitle}
                  p={0}
                  textTransform="uppercase"
                  {...(titleCallout && { fontWeight: 'semibold' })}
                >
                  <Flex align="start" gap={0.5}>
                    {tabTitle}
                    {titleCallout && (
                      <Text color="fill.new" textStyle="sBold">
                        {titleCallout}
                      </Text>
                    )}
                  </Flex>
                </Tab>
              ))}
            </TabList>
          )}
          <TabPanels>
            {tabs.map(({ cards: tabsCards }, index) => (
              <TabPanel
                alignItems="center"
                as={Flex}
                direction="column"
                gap={2}
                key={`highlighted-cards-${index}`}
                p={0}
                pt={3}
              >
                {tabsCards.map(renderCard)}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      ) : (
        cards && (
          <SimpleGrid columns={[1, null, 2, 3]} spacing={2}>
            {cards.map(renderCard)}
          </SimpleGrid>
        )
      )}
      {button && <Button w={['full', null, 'auto']} {...button} />}
    </ModuleWrapper>
  );
});
